import { useState, useEffect } from "react";
import { Link, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import QrScan from "../components/QrScan";
import DocumentUploader from "../components/DocumentUploader";
import { 
  ArrowRightIcon, 
  QrCodeIcon, 
  KeyIcon, 
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  BuildingStorefrontIcon
} from "@heroicons/react/24/outline";
import { publicAPI } from "../api/public";

export default function SenderPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [shopId, setShopId] = useState<string>("");
  const [shopName, setShopName] = useState<string>("");
  const [step, setStep] = useState<'scan' | 'upload'>('scan');
  const [scanMode, setScanMode] = useState<'camera' | 'manual'>('camera');
  const [manualCode, setManualCode] = useState<string>("");
  const [loading, setLoading] = useState(!!searchParams.get('shop'));
  const [error, setError] = useState<string>("");

  const initialSenderInfo = location.state?.senderName 
    ? { name: location.state.senderName, phone: '', email: '' } 
    : undefined;

  // Handle shop ID from URL query parameter (?shop=123 or ?shop=ULID)
  useEffect(() => {
    let shopIdFromUrl = searchParams.get('shop');
    
    if (!shopIdFromUrl) {
      const params = new URLSearchParams(window.location.search);
      shopIdFromUrl = params.get('shop');
    }

    if (shopIdFromUrl) {
      loadShopInfo(shopIdFromUrl);
    } else if (step === 'upload' && !shopId) {
      setStep('scan');
    }
  }, [searchParams]);

  const loadShopInfo = async (id: string) => {
    const cleanId = id.trim();
    if (!cleanId) {
      setError("Please enter a valid shop code");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await publicAPI.getShopInfo(cleanId);
      const shopData = response as any;

      if (!shopData) {
        throw new Error('Invalid shop data received');
      }

      let extractedName = shopData.shop_name || shopData.name || shopData.shopName;
      if (!extractedName && shopData.data) {
        extractedName = shopData.data.shop_name || shopData.data.name || shopData.data.shopName;
      }

      setShopId(cleanId);
      setShopName(extractedName || "Print Shop");
      setStep('upload');
    } catch (err: any) {
      const errorDetail = err?.response?.data?.detail;
      if (errorDetail === 'Shop is currently closed') {
        setError("Shop is currently closed. Please try another shop.");
      } else {
        setError(errorDetail || err?.message || 'Shop not found. Check code & try again.');
      }
      setShopId("");
      setStep('scan');
    } finally {
      setLoading(false);
    }
  };

  const handleQrScan = (value: string) => {
    try {
      const qrText = String(value).trim();
      if (!qrText) return;

      let extractedId = "";

      if (qrText.startsWith('http')) {
        try {
          const url = new URL(qrText);
          extractedId = url.searchParams.get('shop') || "";
          if (!extractedId) {
            const pathParts = url.pathname.split('/').filter(p => p);
            if (pathParts.length >= 2 && (pathParts[0] === 'shop' || pathParts[0] === 'send')) {
              extractedId = pathParts[1];
            }
          }
        } catch (e) {
          console.warn('Invalid URL in QR code', e);
        }
      }

      if (!extractedId) {
        const ulidMatch = qrText.match(/[0-9A-Z]{26}/i);
        if (ulidMatch) extractedId = ulidMatch[0];
      }

      if (extractedId) {
        loadShopInfo(extractedId);
      } else {
        setError('Invalid QR code format');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to scan');
    }
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualCode.trim()) {
      loadShopInfo(manualCode.trim());
    }
  };

  const handleBackToScan = () => {
    setShopId("");
    setShopName("");
    setStep('scan');
    setError("");
  };

  if (step === 'scan') {
    return (
      <div className="min-h-screen bg-slate-950 text-white font-sans flex flex-col justify-between relative overflow-hidden pb-24 md:pb-8">

        {/* Floating Top Header */}
        <header className="relative z-50 flex items-center justify-between px-5 pt-safe-top pt-4">
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 text-white transition-all active:scale-90"
            aria-label="Back to Home"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>

          {/* Brand Pill with Logo */}
          <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 shadow-lg">
            <img src="/logo-icon.svg" alt="Prindox" className="w-5 h-5 object-contain" />
            <span className="text-white text-xs font-black tracking-wider uppercase">Prindox</span>
          </div>

          <div className="w-10" />
        </header>

        {/* Mode Switcher Tabs */}
        <div className="relative z-40 px-6 pt-3 pb-2 max-w-sm mx-auto w-full">
          <div className="bg-white/10 backdrop-blur-md p-1 rounded-2xl border border-white/10 flex items-center gap-1 shadow-lg">
            <button
              onClick={() => { setScanMode('camera'); setError(""); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                scanMode === 'camera'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              <QrCodeIcon className="w-4 h-4" />
              <span>Camera Scan</span>
            </button>
            <button
              onClick={() => { setScanMode('manual'); setError(""); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                scanMode === 'manual'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              <KeyIcon className="w-4 h-4" />
              <span>Enter Code</span>
            </button>
          </div>
        </div>

        {/* Center Scanner / Manual Input Area */}
        <div className="flex-1 flex flex-col justify-center items-center px-5 relative my-auto">
          {/* Status Overlay */}
          {(loading || error) && (
            <div className="w-full max-w-sm mb-4 z-50">
              {loading ? (
                <div className="flex items-center justify-center gap-3 bg-blue-600/90 backdrop-blur-md text-white px-5 py-3 rounded-2xl shadow-xl">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span className="text-xs font-black uppercase tracking-widest">Connecting to Shop...</span>
                </div>
              ) : error ? (
                <div className="flex items-center gap-3 bg-red-600/90 backdrop-blur-md border border-white/10 px-4 py-3 rounded-2xl shadow-xl">
                  <ExclamationTriangleIcon className="w-5 h-5 text-white shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-xs font-black uppercase tracking-tight">Notice</p>
                    <p className="text-white/90 text-xs font-medium">{error}</p>
                  </div>
                  <button 
                    className="ml-auto text-white/70 hover:text-white text-sm font-black p-1" 
                    onClick={() => setError("")}
                  >
                    ✕
                  </button>
                </div>
              ) : null}
            </div>
          )}

          {scanMode === 'camera' ? (
            <div className="w-full max-w-sm aspect-square relative rounded-3xl overflow-hidden shadow-2xl border border-white/15 bg-black">
              <QrScan onScan={handleQrScan} />
            </div>
          ) : (
            <div className="w-full max-w-sm bg-slate-900/90 backdrop-blur-xl border border-white/15 rounded-3xl p-6 shadow-2xl animate-slideUp">
              <div className="w-14 h-14 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-400">
                <BuildingStorefrontIcon className="w-7 h-7" />
              </div>
              <h2 className="text-lg font-black text-center text-white tracking-tight mb-1">
                Enter Shop ID
              </h2>
              <p className="text-slate-400 text-xs text-center font-medium mb-6">
                Type the shop code shown at the counter or on receipt
              </p>

              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                    Shop Code or ULID
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. SHOP_01H... or 1"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 focus:border-blue-500 rounded-2xl px-4 py-3.5 text-sm font-bold text-white placeholder:text-slate-500 outline-none transition-all tracking-wider"
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  disabled={!manualCode.trim() || loading}
                  className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl transition-all ${
                    !manualCode.trim() || loading
                      ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-500 text-white active:scale-95 shadow-blue-500/20'
                  }`}
                >
                  {loading ? 'Verifying...' : 'Connect to Shop'}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Bottom Actions Card */}
        <div className="relative z-40 px-5 pt-2 max-w-sm mx-auto w-full">
          <Link
            to="/find-shops"
            className="flex items-center justify-between bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-4 group hover:bg-white/15 transition-all active:scale-95 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <p className="text-white text-xs font-black uppercase tracking-tight">Not at a counter?</p>
                <p className="text-white/50 text-[10px] font-medium">Browse nearby partner print shops</p>
              </div>
            </div>
            <ArrowRightIcon className="w-4 h-4 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col pb-24 md:pb-12">
      {/* Sticky Header */}
      <header className="bg-white px-5 py-4 flex items-center justify-between border-b border-slate-100 shadow-sm sticky top-0 z-50">
        <button 
          onClick={handleBackToScan}
          className="w-10 h-10 flex items-center justify-center rounded-2xl bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all active:scale-90"
          aria-label="Change Shop"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        
        <div className="text-center px-3 min-w-0 flex-1">
          <div className="text-[9px] font-black uppercase tracking-[0.2em] text-blue-600 leading-none mb-1">
            Connected Shop
          </div>
          <h1 className="text-sm font-black text-slate-900 truncate">
            {shopName || "Print Shop"}
          </h1>
        </div>

        <div className="w-10 h-10 flex items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
          <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse"></div>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 max-w-lg mx-auto w-full px-4 sm:px-6 py-6">
        {!shopId ? (
          <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 text-center shadow-xl shadow-slate-200/50 animate-slideUp">
            <div className="w-16 h-16 bg-amber-50 rounded-3xl flex items-center justify-center mx-auto mb-5 text-amber-500">
              <ExclamationTriangleIcon className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Session Expired</h3>
            <p className="text-xs text-slate-500 mt-2 mb-6 leading-relaxed">
              Shop connection lost. Please scan or enter shop code again.
            </p>
            <button 
              onClick={handleBackToScan}
              className="w-full bg-slate-900 text-white font-black py-4 rounded-2xl shadow-xl hover:bg-slate-800 transition-all active:scale-95 uppercase tracking-widest text-xs"
            >
              Back to Scanner
            </button>
          </div>
        ) : (
          <div className="animate-slideUp">
            <DocumentUploader 
              shopId={shopId} 
              shopName={shopName} 
              initialSenderInfo={initialSenderInfo} 
            />
          </div>
        )}
      </main>
    </div>
  );
}
